/**
 * Liste du parc, triable par colonne et filtrable par une recherche unique multi-colonnes.
 *
 * Le parc se compte en dizaines de GRV à cette échelle (`EcranCarte.tsx` charge déjà tout le parc en
 * une page pour la même raison) : une recherche vraiment transversale suppose que toutes les lignes
 * soient chargées, ce qu'une pagination par curseur empêcherait — elle ne porterait que sur la page
 * courante. Le tri et la recherche restent donc côté client, sur le parc chargé en une fois.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { Chargement, Erreur, Localisation, Vide } from "../../shared/composants/Etats";
import { EnTeteTriable, useTrie, useTriTableau } from "../../shared/composants/tableauTriable";
import { useIdentite } from "../../shared/auth/contexte";
import { formaterDate } from "../../shared/composants/formatage";
import { BoutonExporterCsv } from "../../shared/composants/exportCsv";
import type { FicheGrv } from "../../shared/api/types";
import { useParc } from "./requetes";

function localisationTexte(fiche: FicheGrv): string {
  if (!fiche.zone_courante_nom) return "position inconnue";
  return fiche.localisation_agregee
    ? `${fiche.zone_courante_nom} (localisation agrégée)`
    : fiche.zone_courante_nom;
}

const TAILLE_PARC = 200;

type Colonne = "numero_grv" | "statut" | "client" | "localisation" | "communication" | "batterie";

function valeurDe(fiche: FicheGrv, colonne: Colonne): string | number | null | undefined {
  switch (colonne) {
    case "numero_grv":
      return fiche.numero_grv;
    case "statut":
      return fiche.statut_operationnel;
    case "client":
      return fiche.client_id;
    case "localisation":
      return fiche.zone_courante_nom;
    case "communication":
      return fiche.derniere_communication;
    case "batterie":
      return fiche.batterie_pct;
  }
}

function texteRecherchable(fiche: FicheGrv): string {
  return [
    fiche.numero_grv,
    fiche.statut_operationnel,
    fiche.client_id,
    fiche.zone_courante_nom,
    fiche.derniere_communication,
    fiche.batterie_pct != null ? `${fiche.batterie_pct} %` : null,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

export function EcranListeGrv() {
  const identite = useIdentite();
  const cloisonne = identite.clientId !== null;

  const [recherche, definirRecherche] = useState("");
  const { tri, basculerTri } = useTriTableau<Colonne>("numero_grv");

  const requete = useParc({ pageSize: TAILLE_PARC });

  const parc = useMemo(() => requete.data?.data ?? [], [requete.data]);

  const filtre = useMemo(() => {
    const terme = recherche.trim().toLowerCase();
    if (!terme) return parc;
    return parc.filter((fiche) => texteRecherchable(fiche).includes(terme));
  }, [parc, recherche]);

  const triees = useTrie(filtre, tri, valeurDe);

  return (
    <section>
      <h1>Parc GRV</h1>

      <div className="barre-de-filtres">
        <label htmlFor="recherche-parc">Rechercher</label>
        <input
          id="recherche-parc"
          type="search"
          placeholder="numéro, statut, client, localisation…"
          value={recherche}
          onChange={(evenement) => definirRecherche(evenement.target.value)}
        />
        {!cloisonne && <small>Le parc complet est affiché, tous clients confondus.</small>}

        <BoutonExporterCsv
          nomFichier="parc-grv"
          colonnes={[
            { libelle: "Numéro", valeur: (f: FicheGrv) => f.numero_grv },
            { libelle: "Statut", valeur: (f: FicheGrv) => f.statut_operationnel },
            ...(cloisonne ? [] : [{ libelle: "Client", valeur: (f: FicheGrv) => f.client_id }]),
            { libelle: "Localisation", valeur: localisationTexte },
            {
              libelle: "Dernière communication",
              valeur: (f: FicheGrv) =>
                f.derniere_communication ? formaterDate(f.derniere_communication) : "",
            },
            {
              libelle: "Batterie (%)",
              valeur: (f: FicheGrv) => f.batterie_pct ?? null,
            },
          ]}
          lignes={triees}
        />
      </div>

      {requete.isPending && <Chargement libelle="Chargement du parc…" />}
      {requete.isError && <Erreur erreur={requete.error} reessayer={() => void requete.refetch()} />}

      {requete.data && triees.length === 0 && (
        <Vide libelle="Aucun GRV ne correspond à ces critères." />
      )}

      {requete.data && triees.length > 0 && (
        <table className="tableau">
          <thead>
            <tr>
              <EnTeteTriable colonne="numero_grv" libelle="Numéro" tri={tri} basculerTri={basculerTri} />
              <EnTeteTriable colonne="statut" libelle="Statut" tri={tri} basculerTri={basculerTri} />
              {!cloisonne && (
                <EnTeteTriable colonne="client" libelle="Client" tri={tri} basculerTri={basculerTri} />
              )}
              <EnTeteTriable
                colonne="localisation"
                libelle="Localisation"
                tri={tri}
                basculerTri={basculerTri}
              />
              <EnTeteTriable
                colonne="communication"
                libelle="Dernière communication"
                tri={tri}
                basculerTri={basculerTri}
              />
              <EnTeteTriable colonne="batterie" libelle="Batterie" tri={tri} basculerTri={basculerTri} />
            </tr>
          </thead>
          <tbody>
            {triees.map((fiche) => (
              <tr key={fiche.numero_grv}>
                <th scope="row">
                  <Link to={`/grv/${encodeURIComponent(fiche.numero_grv)}`}>
                    {fiche.numero_grv}
                  </Link>
                </th>
                <td>{fiche.statut_operationnel}</td>
                {!cloisonne && <td>{fiche.client_id}</td>}
                <td>
                  <Localisation
                    zone={fiche.zone_courante_nom}
                    agregee={fiche.localisation_agregee}
                  />
                </td>
                <td>{formaterDate(fiche.derniere_communication)}</td>
                <td>
                  {fiche.batterie_pct === null || fiche.batterie_pct === undefined
                    ? "—"
                    : `${fiche.batterie_pct} %`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
